import React, { useRef, useState, DragEvent } from "react";
import { toast } from "react-hot-toast";
import { DocumentIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

export const MAX_REFERENCE_PDF_MB = 50;

interface ReferencePdfPickerProps {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Optional "completed example" of a form. The AI compares it with the blank
 * form to learn what goes in each field. Only its page images go to the one
 * analysis run; the file is never uploaded or stored.
 */
const ReferencePdfPicker: React.FC<ReferencePdfPickerProps> = ({
  file,
  onChange,
  disabled = false,
  className = "",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const accept = (picked: File | undefined) => {
    if (!picked) return;
    if (picked.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }
    if (picked.size > MAX_REFERENCE_PDF_MB * 1024 * 1024) {
      toast.error(`File size must be less than ${MAX_REFERENCE_PDF_MB}MB`);
      return;
    }
    onChange(picked);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!disabled) accept(e.dataTransfer.files[0]);
  };

  return (
    <div className={className} data-tour="form-reference-picker">
      <p className="font-medium text-sm pb-1 m-0">Completed example (optional)</p>
      <p className="text-xs text-base-content/60 mb-2">
        Add the same form filled out and the AI will compare the two to write better field instructions. Use a
        sample with fake or redacted details if you can. It is used for this analysis only and is not saved.
      </p>
      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-base-300 bg-base-200 p-2">
          <div className="flex items-center gap-2 min-w-0">
            <DocumentIcon className="h-5 w-5 shrink-0 text-blue-400" />
            <span className="text-sm truncate">{file.name}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
            disabled={disabled}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Remove completed example"
          >
            <TrashIcon className="h-4 w-4 text-red-500" />
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-disabled={disabled}
          className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed p-3 text-sm transition-colors ${
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200"
          } ${
            isDragOver
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
              : "border-gray-300 dark:border-base-300 text-base-content/70"
          }`}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragOver(false);
          }}
          onDrop={handleDrop}
        >
          <PlusIcon className="h-4 w-4" />
          Add a filled-out copy (PDF)
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => accept(e.target.files?.[0])}
      />
    </div>
  );
};

export default ReferencePdfPicker;
